import { useQuery } from '@hooks/reactQuery';

import { getGoldenSettingsMavenirConfigTree } from '@api/goldenSettings';

import { extractQueryParams } from '@utils/common';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { convertToMavenirFlatTreeData } from '@utils/treeFormatter/treeFormatter';

import { TGoldenSettingsConfigTreeDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';
import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EVendorsValues } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import { refetchOptions } from '@constants/common';
import { GOLDEN_SETTING_ON_SELECTED_DATE_MAVENIR } from '@constants/queryKeys';

interface IUseQueryGoldenSettingsOnSelectedDateMavenirReturnType {
	rawConfigDataOnSelectedDate?: Record<string, any>;
	goldenSettingsOnSelectedDateMavenir?: IFlatMoTreeNode[];
	isLoadingGoldenSettingsOnSelectedDateMavenir: boolean;
}

const useQueryGoldenSettingsOnSelectedDateMavenir = (
	params: Partial<TGoldenSettingsConfigTreeDTO>,
): IUseQueryGoldenSettingsOnSelectedDateMavenirReturnType => {
	const {
		vendor,
		dateText,
		nodes,
		type,
		version,
		importId,
	} = params;

	const isEnabled = vendor === EVendorsValues.mavenir
		? !!vendor && !!nodes && !!type && !!version && dateText
		: !!vendor && !!importId;

	const {
		data,
		isInitialLoading: isLoadingGoldenSettingsOnSelectedDateMavenir,
	} = useQuery({
		queryKey: [
			GOLDEN_SETTING_ON_SELECTED_DATE_MAVENIR,
			params,
		],
		queryFn: extractQueryParams(getGoldenSettingsMavenirConfigTree),
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => item.code === EErrorCodes.goldenSettingsNotSet)) {
				handleErrorFromMessage(err);
			}
		},
		enabled: [isEnabled],
		...refetchOptions,
	});

	const goldenSettingsOnSelectedDateMavenir = data
		? convertToMavenirFlatTreeData({
			data: data ? [data.moTree] : [],
			isSingleSelectMode: true,
			isDefaultNodeChecked: true,
		})
		: undefined;

	const rawConfigDataOnSelectedDate = data;

	return {
		rawConfigDataOnSelectedDate,
		goldenSettingsOnSelectedDateMavenir,
		isLoadingGoldenSettingsOnSelectedDateMavenir,
	};
};

export default useQueryGoldenSettingsOnSelectedDateMavenir;
